// Self-hosted OTA live updates — the logic half (no React in here).
//
// Ships JS/CSS changes to installed Android apps without a Play Store release,
// using @capgo/capacitor-updater in MANUAL mode against our own server. The paid
// Capgo cloud is not involved: we host latest.json + the zips ourselves and do
// the version check here.
//
// ⚠️ WHAT CAN AND CANNOT GO OTA
// An OTA bundle replaces the WEB assets only. Anything native — a new Capacitor
// plugin, a permission, an SDK level, the app icon, versionCode — needs a real
// Play release. Shipping web code that calls a plugin the installed APK doesn't
// have will simply fail at runtime on every updated device.
//
// The flow each launch (and each resume):
//   1. notifyAppReady()  — MANDATORY. After applying a bundle the plugin arms a
//      ~10 s watchdog; if this call doesn't land it assumes the bundle crashed
//      and rolls back. Miss it and every OTA silently reverts.
//   2. GET <base>/latest.json via CapacitorHttp (native — no CORS on the server).
//   3. If its version is strictly greater than the floor, download() then
//      next() — applied on the NEXT cold start, never mid-session.
//
// The floor is max(running bundle version, native versionName). Flooring at the
// APK version is what makes it impossible for a stale or wrong latest.json to
// downgrade someone below what Play installed.
//
// THE OTHER DIRECTION — `minNative` in the manifest. The floor above stops a
// bundle that is too OLD. Nothing stopped a bundle that is too NEW: web code
// calling a plugin the installed APK does not contain, which fails at runtime on
// every phone that takes it, with no way back except another OTA. A bundle can
// now declare the minimum APK it needs, and phones below that simply do not take
// it — they stay on what works until Play catches up.
//
// It is a DECLARATION, not something derivable: whether a bundle truly requires
// a plugin depends on whether it degrades without it. A release that shows
// "update the app" where a scan would be is safe on an older APK; one that
// crashes is not. So the release engineer sets it (package.json "otaMinNative")
// and this enforces it.
//
// Everything fails open: any network, parse or plugin error is swallowed. An OTA
// problem must never stop the app from starting.

import { Capacitor, CapacitorHttp } from '@capacitor/core';

export const OTA_BASE = (
  process.env.REACT_APP_OTA_URL || ''
).replace(/\/$/, '');

export function otaSupported() {
  try {
    return Capacitor.getPlatform() === 'android' && !!OTA_BASE;
  } catch (e) {
    return false;
  }
}

// "1.5.4" > "1.5.3". Numeric segments only — no pre-release tags.
export function semverGt(a, b) {
  const pa = String(a).split('.').map((n) => parseInt(n, 10) || 0);
  const pb = String(b).split('.').map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < 3; i += 1) {
    if ((pa[i] || 0) > (pb[i] || 0)) return true;
    if ((pa[i] || 0) < (pb[i] || 0)) return false;
  }
  return false;
}

async function fetchManifest() {
  // CapacitorHttp is the NATIVE http stack, so it bypasses the WebView's CORS
  // rules — the OTA host needs no Access-Control-Allow-Origin header. The
  // cache-buster stops a CDN or proxy pinning an old manifest.
  const res = await CapacitorHttp.get({
    url: `${OTA_BASE}/latest.json?t=${Date.now()}`,
    headers: { 'Cache-Control': 'no-cache' },
  });
  if (res.status !== 200) return null;
  let manifest = res.data;
  if (typeof manifest === 'string') {
    try { manifest = JSON.parse(manifest); } catch (e) { return null; }
  }
  if (!manifest || !manifest.version || !manifest.url) return null;
  return manifest;
}

// Check once and, if there is something newer, stage it for the next restart.
// Returns the staged version string, or null.
export async function checkForUpdate(CapacitorUpdater) {
  try {
    const manifest = await fetchManifest();
    if (!manifest) return null;

    const builtin = await CapacitorUpdater.getBuiltinVersion()
      .catch(() => ({ version: '0.0.0' }));
    const cur = await CapacitorUpdater.current();
    const curRaw = cur && cur.bundle && cur.bundle.version;
    // current() reports the literal 'builtin' for the shipped bundle.
    const curVersion = (curRaw && curRaw !== 'builtin') ? curRaw : builtin.version;
    const floor = semverGt(curVersion, builtin.version) ? curVersion : builtin.version;

    if (!semverGt(manifest.version, floor)) return null;

    // Too new for this APK's native side — skip, and say so loudly enough to be
    // findable, because from the user's side this looks like OTA has stopped.
    // builtin.version is the APK's versionName; the running bundle is
    // irrelevant here, since a live update never changes the native code.
    if (manifest.minNative && semverGt(manifest.minNative, builtin.version)) {
      // eslint-disable-next-line no-console
      console.log(
        `[OTA] ${manifest.version} needs app ${manifest.minNative}, `
        + `this is ${builtin.version} — waiting for a Play update`,
      );
      return null;
    }

    const bundle = await CapacitorUpdater.download({
      url: manifest.url,
      version: manifest.version,
      ...(manifest.checksum ? { checksum: manifest.checksum } : {}),
    });
    // next() = apply on next cold start. set() would reload the webview
    // immediately and yank the screen out from under whatever the user is doing.
    await CapacitorUpdater.next({ id: bundle.id });
    // eslint-disable-next-line no-console
    console.log(`[OTA] downloaded ${manifest.version}; applies on next restart`);
    return manifest.version;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[OTA] update check skipped:', (err && err.message) || err);
    return null;
  }
}

// Load the native plugin. Dynamic import so a web/desktop build never tries to
// resolve the native module.
export async function loadUpdater() {
  const mod = await import('@capgo/capacitor-updater');
  return mod.CapacitorUpdater;
}
